<template>
    <div>
        <div class="bg-growth rounded-full text-white p-2 fixed z-50 top-5 px-6 left-1/2 z-40" v-if="saved">{{ $t('saved') }}</div>

        <div v-if="loading" class="text-center p-3 mt-7">
            <font-awesome-icon icon="fas fa-spinner" class="text-2xl animate-spin" />
        </div>

        <div v-if="Array.isArray(session)" class="overflow-auto">
            <slot v-for="(as,asI) in session" :key="asI">
                <div v-if="shouldShow(as)"
                    class="p-2 px-5 border border-sepBorder dark:border-dark.sepBorder dark:bg-dark.tableBG rounded mb-2 flex justify-between" :class="sessionStatus && sessionStatus[asI]?.refreshing ? 'bg-gray-100' : ''">
                    <div v-if="sessionStatus && sessionStatus[asI]?.refreshing" class="text-center w-full py-2" style="padding-top:9px">
                        <font-awesome-icon icon="fas fa-spinner" class="animate-spin" />
                    </div>
                    <slot v-else>
                        <div class="leading-5">
                            <span class="capitalize font-semibold">{{as.display_name}}</span><br />
                            <span style="font-size:13px">
                                <span v-if="as.on_session_username && as.locked" :title="as.on_session_email">
                                    <span class="text-gray-500"> {{ $t('in_use_by') }}</span> <strong>{{as.on_session_username}}</strong>
                                </span>
                                <span v-else-if="as.ID" class="text-growth">{{ $t('available') }}</span>
                                <span v-else class="text-gray-500 dark:text-gray-400">{{ $t('not_created') }}</span>
                            </span>
                        </div>
                        <div class="flex" v-if="permissions">
                            <div v-if="as.ID != null && !sessionStatus[asI].loading" class="mt-2 grid gap-2 justify-between mr-3 text-center" 
                                :class="actionsColumns">
                                <tooltip v-if="permissions && $getPermission(permissions,28).is_granted" :msg="$t('delete_session')" icon="fas fa-trash-alt" iconClass="text-decline" @click="trashSession(asI)" />
                                <span v-if="permissions && $getPermission(permissions,29).is_granted" class="cursor-pointer">
                                    <tooltip @click="UnlockSession(asI, true)" v-if="as.locked" :msg="$t('unlock_this_session')" icon="fas fa-lock-open" iconClass="text-growth" />
                                    <tooltip v-else @click="UnlockSession(asI, false)" :msg="$t('lock_this_session')" icon="fas fa-lock" classBand="cursor-default" iconClass="text-yellow-500" />
                                </span>
                                <span class="cursor-pointer" @click="refreshSession(asI)">
                                    <tooltip :msg="$t('refresh_session')" icon="fas fa-arrows-rotate" iconClass="dark:text-dark.textOnBkg" />
                                </span>
                            </div>
                            <div v-if="as.ID == null && !$getPermission(permissions,28).is_granted" class="cursor-pointer p-1 px-2 rounded w-28 text-center border border-sepBorder text-xs">
                                {{ $t('not_available_no_permission') }}
                            </div>
                            <div v-else @click="openBrowser(as, asI)" class="p-2 rounded w-28 text-center whitespace-nowrap font-semibold"
                                :class="as.ID == null ? 'bg-growth text-white cursor-pointer' : (as.locked ? 'cursor-not-allowed bg-gold text-black' : 'cursor-pointer bg-button text-white')">
                                <font-awesome-icon icon="fas fa-spinner" class="animate-spin dark:text-dark.textOnBkg" v-if="sessionStatus[asI].loading"/>
                                <slot v-else>{{ as.ID == null ? ($t('create')) : (as.locked ? $t('locked') : $t('open')) }}</slot>
                            </div>
                        </div>
                    </slot>
                </div>
            </slot>
        </div>
    </div>
</template>
<script setup>
    import Tooltip from '@/components/toolTip.vue';
    import { fetch_all_sessions_of_one_model, getSession, deleteSession } from '@/api/session.js';
</script>
<script>
    export default {
        components: {
            Tooltip
        },
        props: ['model_data', 'permissions'],
        emits: ['close'],
        data() {
            return {
                isElectron: this.$cookies.get('desktop_app_token') == import.meta.env.VITE_API_INTERNAL_KEY_LIST,
                loading:false,
                session: [],
                saved:false,
                sessionStatus: [],
                pendingSessions: []
            }
        },
        computed: {
            actionsColumns() {
                let l = 1;
                if (this.permissions && this.$getPermission(this.permissions,28).is_granted) { l++; }
                if (this.permissions && this.$getPermission(this.permissions,29).is_granted) { l++; }
                return 'grid-cols-' + l;
            },
            shouldShow() {
                return (as) => {
                    if (!this.permissions) {
                        return false;
                    }
                    if (as && as.PermissionsFor) {
                        if (as.PermissionsFor == 'admin' && !this.$getPermission(this.permissions, 24).is_granted) {
                            return false;
                        }
                        if (as.PermissionsFor == 'sexting' && !this.$getPermission(this.permissions, 25).is_granted) {
                            return false;
                        }
                        if (as.PermissionsFor == 'droper' && !this.$getPermission(this.permissions, 26).is_granted) {
                            return false;
                        }
                        if (as.PermissionsFor == 'backup' && !this.$getPermission(this.permissions, 27).is_granted) {
                            return false;
                        }
                        return true;
                    } else {
                        return false;
                    }
                }
            }
        },
        methods: {
            async openBrowser(picked_session, index) {
                if (picked_session.ID == null && !this.$getPermission(this.permissions,28).is_granted) {
                    return alert(this.$t('no_permission'));
                }
                if (picked_session.locked) {
                    return alert(this.$t('session') + ' ' + this.$t('in_use_by') + ' ' + picked_session.on_session_username + '. Email: ' + picked_session.on_session_email);
                }
                if (!this.isElectron) {
                    alert('This feature is only available in the desktop app.');
                    return;
                }
                try {
                    this.sessionStatus[index].loading = true;
                    const proxy = null; // Proxy server if needed
                    //action, proxy, profile, session, userToken
                    let action = picked_session.ID ? 'session' : 'login';
                    const session_data = await getSession(picked_session.ID, this.model_data.platform);
                    
                    const tempID = picked_session.ID ? null : `temp-${Date.now()}`;
                    if (!picked_session.ID) {
                        this.pendingSessions.push({ tempID, index });
                        this.session[index].ID = tempID; // Temporarily assign the ID
                    }

                    const result = await window.electron.openPuppeteerBrowser({
                        action,
                        proxy,
                        profile: JSON.parse(JSON.stringify(this.model_data)),
                        session: JSON.parse(JSON.stringify(session_data)),
                        type: picked_session.PermissionsFor,
                        userToken: this.$cookies.get('userToken'),
                        tempID // Sending a tempID to the Electron app - it will be used to identify the session
                    });

                } catch (error) {
                    this.sessionStatus[index].loading = false;
                }
            },
            async trashSession(index) {
                //finish this code
                this.sessionStatus[index].loading = true;
                if (confirm(this.$t('delete_session_confirm'))) {
                    let dResult = await deleteSession(this.session[index].ID, this.$cookies.get('userToken'), this.$cookies.get('c_id'));
                    if (dResult) {
                        let ts = this.session[index];
                        this.session[index] = {
                            ID: null,
                            PermissionsFor: ts.PermissionsFor,
                            locked: 0,
                            blocked: 0,
                            createdAt: null,
                            display_name: ts.display_name,
                            on_session_username: null,
                            on_session_email: null
                        };
                        this.sessionStatus[index].loading = false;
                    }
                }
                setTimeout(() => this.sessionStatus[index].loading = false, 10000);
            },
            switchSession(i1,i2,i3) {
                let tag = prompt(this.$t('switch_session_confirm'), this.$t('sexter'));
                if (tag == 'sexter' || tag == 'droper' || tag == 'admin' || tag == 'backup') {
                    this.$axios.post("settingspost/", {
                        action: 'switchSession',
                        sessionID: this.session[i1].accounts[i2].sessions[i3].ID,
                        newNote: tag,
                        userToken: this.$cookies.get('userToken'),
                        companyID: this.$cookies.get('c_id')
                    }).then((result) => {
                        this.refreshSession(0,i2,i3);
                    }).finally(() => this.loading = false);
                } else {
                    alert(this.$t('session_unrecognized'));
                }
            },
            UnlockSession(index, unlock) {
                if (confirm(unlock ? this.$t('session_unlock_warning') : this.$t('session_lock_warning'))) {
                    this.$axios.post("settingspost/", {
                        action: 'unlockSession',
                        lock: unlock ? '0' : '1',
                        sessionID: this.session[index].ID,
                        userToken: this.$cookies.get('userToken'),
                        companyID: this.$cookies.get('c_id'),
                        platform: this.model_data.platform
                    }).then((result) => {
                        this.refreshSession(index);
                    }).finally(() => this.loading = false);
                }
            },
            refreshSession(index) {
                this.sessionStatus[index].refreshing = true;
                this.$axios.get("settingsget/", {
                    params: {
                        action: 'refreshSession',
                        sessionID: this.session[index].ID,
                        userToken: this.$cookies.get('userToken'),
                        companyID: this.$cookies.get('c_id'),
                        platform: this.model_data.platform
                    }
                }).then((result) => {
                    this.session[index] = result.data
                }).finally(() => setTimeout(() => {
                    this.loading = false;
                    this.sessionStatus[index].refreshing = false;
                }, 500));
            }
        },
        async created() {
            if (!this.isElectron) {
                this.$router.go(-1);                
            }
            this.loading = true;
            this.session = await fetch_all_sessions_of_one_model(
                this.model_data.free ? this.model_data.free.id : this.model_data.id, 
                this.$cookies.get('userToken'),
                this.$cookies.get('c_id'), 
                this.model_data.platform
            );
            this.loading = false;

            this.sessionStatus = Array.isArray(this.session) ? this.session.map(() => ({
                loading: false,
                refreshing: false,
            })) : [];

            if (this.isElectron) {
                window.electron.onBrowserOpened((data) => {
                    // Update session status in your Vue app
                    let pendingIndex = this.pendingSessions.findIndex(session => session.tempID === data.tempID);
                    if (pendingIndex !== -1) {
                    } else {
                        // Normal case, find by ID
                        let index = this.session.findIndex(session => session.ID === data.sessionID);
                        if (index !== -1) {
                            this.session[index].locked = 1; // Lock session
                            this.sessionStatus[index].loading = false;
                        }
                    }
                });

                window.electron.onBrowserClosed((data) => {
                    // Handle any additional cleanup or UI updates
                    let pendingIndex = this.pendingSessions.findIndex(session => session.tempID === data.tempID);
                    if (pendingIndex !== -1) {
                        let index = this.pendingSessions[pendingIndex].index;
                        this.session[index].locked = 0; // Lock session
                        this.sessionStatus[index].loading = false;
                        this.pendingSessions.splice(pendingIndex, 1); // Remove from pending
                    } else {
                        // Normal case, find by ID
                        let index = this.session.findIndex(session => session.ID === data.sessionID);
                        if (index !== -1) {
                            this.session[index].locked = 0; // Lock session
                            this.sessionStatus[index].loading = false;
                        }
                    }
                });

                window.electron.onBrowserLoggedIn((data) => {
                    // Check for pending sessions
                    let pendingIndex = this.pendingSessions.findIndex(session => session.tempID === data.tempID);
                    if (pendingIndex !== -1) {
                        let index = this.pendingSessions[pendingIndex].index;
                        this.session[index].ID = data.sessionID; // Update session with new ID
                        this.session[index].locked = 1; // Lock session
                        this.sessionStatus[index].loading = false;
                    } else {
                        // Normal case, find by ID
                        let index = this.session.findIndex(session => session.ID === data.sessionID);
                        if (index !== -1) {
                            this.session[index].locked = 1; // Lock session
                            this.sessionStatus[index].loading = false;
                        }
                    }
                });

                window.electron.onBrowserFailed((arg) => {
                    console.error(`Failed to open browser for session ID: ${arg.sessionID}`, arg.error);
                    this.sessionStatus[index].loading = false;
                });
            }
        }
    }
</script>

<style scoped>
  td, th {
    padding:5px;
    border:1px solid rgba(80, 80, 80, 0.2);
    text-align:center;
  }
</style>